import { ChangeEvent, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { GripVertical, Star, Trash2, Upload } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Label } from "@/components/ui/label";
import type { WizardValues } from "./schema";

const MAX = 10;
const ROLE_LABELS = ["Thumbnail", "Detail", "Detail"] as const;
const getRole = (idx: number) => ROLE_LABELS[idx] ?? "Gallery";

interface PendingPhoto {
  file: File;
  preview: string;
  isCover: boolean;
}

interface TileProps {
  photo: PendingPhoto;
  index: number;
  onRemove: () => void;
  onSetCover: () => void;
}

const SortableTile = ({ photo, index, onRemove, onSetCover }: TileProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.preview,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={`relative rounded-card overflow-hidden border ${photo.isCover ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
    >
      <img src={photo.preview} alt="" className="w-full h-28 object-cover" />
      <button
        type="button"
        className="absolute top-1 left-1 p-0.5 bg-black/50 rounded text-white/70 hover:text-white cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <span className="absolute top-1 right-1 text-[9px] font-semibold bg-black/60 text-white px-1.5 py-0.5 rounded">
        {getRole(index)}
      </span>
      <div className="absolute inset-x-0 bottom-0 flex justify-between p-1.5 bg-gradient-to-t from-black/70 to-transparent">
        <button
          type="button"
          onClick={onSetCover}
          className="flex items-center gap-1 text-[11px] text-white"
          title="Set as thumbnail"
        >
          <Star
            className={`w-3.5 h-3.5 ${photo.isCover ? "fill-amber-400 text-amber-400" : ""}`}
          />
          {photo.isCover ? "Thumbnail" : "Set thumbnail"}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="text-white/80 hover:text-white"
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export const ImagesStep = () => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WizardValues>();
  const photos: PendingPhoto[] = watch("pendingPhotos") ?? [];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    return () => {
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    if (!list.length) return;
    const slots = MAX - photos.length;
    const accepted = list.slice(0, Math.max(0, slots)).map((file, i) => ({
      file,
      preview: URL.createObjectURL(file),
      isCover: photos.length === 0 && i === 0,
    }));
    setValue("pendingPhotos", [...photos, ...accepted], {
      shouldValidate: true,
    });
    e.target.value = "";
  };

  const remove = (idx: number) => {
    const next = photos.filter((_, i) => i !== idx);
    URL.revokeObjectURL(photos[idx].preview);
    if (photos[idx].isCover && next.length) next[0].isCover = true;
    setValue("pendingPhotos", next, { shouldValidate: true });
  };

  const setCover = (idx: number) => {
    setValue(
      "pendingPhotos",
      photos.map((p, i) => ({ ...p, isCover: i === idx })),
      { shouldValidate: true },
    );
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = photos.findIndex((p) => p.preview === active.id);
    const newIdx = photos.findIndex((p) => p.preview === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = [...photos];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    if (!reordered.some((p) => p.isCover)) reordered[0].isCover = true;
    setValue("pendingPhotos", reordered, { shouldValidate: true });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Photos</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Optional. Add up to {MAX} photos. First = thumbnail · positions 2-3 =
          detail · rest = gallery. Drag to reorder.
        </p>
      </div>

      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-card p-8 cursor-pointer hover:border-primary/50 transition-colors">
        <Upload className="w-6 h-6 text-muted-foreground" />
        <span className="text-sm font-medium">
          {photos.length >= MAX ? "Maximum reached" : "Click to upload images"}
        </span>
        <span className="text-xs text-muted-foreground">
          {photos.length}/{MAX} added
        </span>
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          disabled={photos.length >= MAX}
          onChange={addFiles}
        />
      </label>

      {errors.pendingPhotos && !Array.isArray(errors.pendingPhotos) ? (
        <p className="text-xs text-destructive">
          {errors.pendingPhotos.message as string}
        </p>
      ) : null}

      {photos.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={photos.map((p) => p.preview)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <SortableTile
                  key={p.preview}
                  photo={p}
                  index={i}
                  onRemove={() => remove(i)}
                  onSetCover={() => setCover(i)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : null}
    </div>
  );
};
